/* pdp-pairings.js — PDP "Farbkombinationen" tab behaviour.

   Wires:
     - Tile media click → opens shared lightbox (window.BfComboLightbox)
       with the curated pairing photo (or partner thumbnail if no curated
       pairing exists, e.g. Holst).
     - "Beide in den Warenkorb" → POSTs both base + partner to /cart/add.js
       and emits boni:cart:added so the cart drawer auto-opens (matches
       the existing PDP main-ATC behaviour, see cart-drawer.js).
     - boni:variant:changed → refetches the section via Shopify's Section
       Rendering API and swaps the strip in-place. Race-guard ensures
       rapid swatch toggling lands on the latest variant.
*/
(function () {
  'use strict';

  const root = document.querySelector('[data-pdp-pairings]');
  if (!root) return;

  let fetchToken = 0;

  // ── KFO curated-pairing photo hydration (Storefront API) ───────────────
  // Liquid for-loops cap at 250 iterations and the kfo_color_pairing
  // dataset exceeds that, so the only way to surface every curated photo
  // is via the Storefront API with cursor pagination. Same pattern as
  // assets/color-combinations.js on the combos overview page. Lookup is
  // bidirectional by lowercase-title key — KFO recycles colour names
  // across yarn lines (Merino "Licorice" + SSH "Licorice"), so title
  // matching surfaces cross-line pairs that strict ID matching misses.

  function pairingKey(a, b) {
    const la = (a || '').toLowerCase().trim();
    const lb = (b || '').toLowerCase().trim();
    return la < lb ? la + '~~~' + lb : lb + '~~~' + la;
  }

  let pairingLookup = null;
  let pairingReady  = null;

  async function fetchAllPairings(shop, token) {
    const endpoint = `https://${shop}/api/2024-10/graphql.json`;
    const query = `query GetPairings($cursor: String) {
      metaobjects(type: "kfo_color_pairing", first: 250, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes { fields { key reference {
          ... on ProductVariant { title }
          ... on MediaImage { image { url(transform: { maxWidth: 600 }) } }
        } } }
      }
    }`;
    const lookup = {};
    let cursor = null, hasMore = true;
    while (hasMore) {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query, variables: { cursor } })
      });
      const data = await res.json();
      const page = data && data.data && data.data.metaobjects;
      if (!page) break;
      page.nodes.forEach(node => {
        const fields = {};
        node.fields.forEach(f => { fields[f.key] = f; });
        const yarnA  = fields.yarn_a && fields.yarn_a.reference && fields.yarn_a.reference.title;
        const yarnB  = fields.yarn_b && fields.yarn_b.reference && fields.yarn_b.reference.title;
        const imgUrl = fields.pairing_image && fields.pairing_image.reference && fields.pairing_image.reference.image && fields.pairing_image.reference.image.url;
        if (yarnA && yarnB && imgUrl) lookup[pairingKey(yarnA, yarnB)] = imgUrl;
      });
      hasMore = page.pageInfo.hasNextPage;
      cursor  = page.pageInfo.endCursor;
    }
    return lookup;
  }

  (function initPairingsFetch() {
    const cfgEl = document.getElementById('kfo-pairing-config');
    if (!cfgEl) return;
    try {
      const cfg = JSON.parse(cfgEl.textContent);
      if (!cfg.shop || !cfg.token) return;
      pairingReady = fetchAllPairings(cfg.shop, cfg.token)
        .then(lookup => { pairingLookup = lookup; applyPairingsToAllTiles(); })
        .catch(() => { pairingLookup = {}; });
    } catch (_) { /* malformed JSON — skip */ }
  })();

  // Walk every tile in the document and upgrade thumbnail + lightbox src
  // to the curated pairing photo when we have one for that pair.
  function applyPairingsToAllTiles() {
    if (!pairingLookup) return;
    document.querySelectorAll('[data-pdp-pairing-tile]').forEach(tile => {
      const baseName    = tile.dataset.baseName;
      const partnerName = tile.dataset.partnerName;
      if (!baseName || !partnerName) return;
      const url = pairingLookup[pairingKey(baseName, partnerName)];
      if (!url) return;
      tile.dataset.pairingImage = url;
      const img = tile.querySelector('.combo-match__media img');
      if (img) img.src = url;
    });
  }

  // ── Scroll affordance ──────────────────────────────────────────────────
  // macOS hides overflow scrollbars by default, so we toggle a fade
  // gradient on the wrapper when the strip actually overflows.

  function updateOverflowAffordance() {
    document.querySelectorAll('[data-pdp-pairings-scroll-wrap]').forEach(wrap => {
      const scroll = wrap.querySelector('[data-pdp-pairings-scroll]');
      if (!scroll) return;
      const hasOverflow = scroll.scrollWidth > scroll.clientWidth + 1;
      const atEnd = Math.ceil(scroll.scrollLeft + scroll.clientWidth) >= scroll.scrollWidth;
      wrap.setAttribute('data-has-overflow', hasOverflow && !atEnd ? 'true' : 'false');
    });
  }

  function bindScrollListeners() {
    document.querySelectorAll('[data-pdp-pairings-scroll]').forEach(scroll => {
      if (scroll.__bfBound) return;
      scroll.__bfBound = true;
      scroll.addEventListener('scroll', updateOverflowAffordance, { passive: true });
    });
  }

  // Run on load, on resize, and after any DOM swap (variant change).
  window.addEventListener('load', () => { bindScrollListeners(); updateOverflowAffordance(); });
  window.addEventListener('resize', updateOverflowAffordance);
  // Fonts can load late and shift tile widths; recheck after a beat.
  setTimeout(() => { bindScrollListeners(); updateOverflowAffordance(); }, 400);

  // ── Tile + bundle click delegation ─────────────────────────────────────

  document.addEventListener('click', e => {
    const container = e.target.closest('[data-pdp-pairings]');
    if (!container) return;

    // Bundle add-to-cart
    const bundleBtn = e.target.closest('[data-pdp-bundle-atc]');
    if (bundleBtn) {
      const tile = bundleBtn.closest('[data-pdp-pairing-tile]');
      if (tile) handleBundleAdd(tile, bundleBtn);
      return;
    }

    // Tile media → lightbox
    const media = e.target.closest('.combo-match__media');
    if (media) {
      const tile = media.closest('[data-pdp-pairing-tile]');
      if (!tile || !window.BfComboLightbox) return;
      const pairingImg = tile.dataset.pairingImage;
      const fallbackImg = media.querySelector('img');
      const src = pairingImg || (fallbackImg && fallbackImg.src);
      if (!src) return;
      const caption = tile.dataset.caption || '';
      window.BfComboLightbox.open(src, caption);
    }
  });

  function handleBundleAdd(tile, btn) {
    if (btn.disabled) return;
    const baseId = parseInt(tile.dataset.baseId, 10);
    const partnerId = parseInt(tile.dataset.partnerId, 10);
    if (!baseId || !partnerId) return;

    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = bfT('adding', 'Wird hinzugefügt …');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [
        { id: baseId, quantity: 1 },
        { id: partnerId, quantity: 1 }
      ]})
    })
      .then(res => {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(() => fetch('/cart.js').then(r => r.json()))
      .then(cart => {
        document.dispatchEvent(new CustomEvent('boni:cart:added', { detail: cart }));
        btn.textContent = bfT('added_check', 'Hinzugefügt ✓');
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1400);
      })
      .catch(() => {
        btn.textContent = bfT('failed_retry', 'Fehlgeschlagen — erneut versuchen');
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2000);
      });
  }

  // ── Variant-change refetch (Section Rendering API) ─────────────────────

  document.addEventListener('boni:variant:changed', e => {
    const variantId = e.detail && e.detail.variantId;
    if (!variantId) return;

    const my = ++fetchToken;
    const url = `${location.pathname}?section_id=product-pairings-tab&variant=${variantId}`;

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(res => res.ok ? res.text() : Promise.reject())
      .then(html => {
        if (my !== fetchToken) return; // stale response, ignore
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const fresh = tmp.querySelector('[data-pdp-pairings]');
        const current = document.querySelector('[data-pdp-pairings]');
        if (!fresh || !current) return;
        current.replaceWith(fresh);
        bindScrollListeners();
        updateOverflowAffordance();
        applyPairingsToAllTiles();
      })
      .catch(() => { /* keep stale strip on error */ });
  });

})();
